
import { defineComponent, computed, ref, unref, onMounted, nextTick } from 'vue'
import { FieldStringOutlined, FileImageOutlined, BuildOutlined, ArrowLeftOutlined } from '@ant-design/icons-vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { pickBy } from 'lodash-es'
import initHotKeys from '@/plugins/hotKeys'
import initContextMenu from '@/plugins/contextMenu'
import { GlobalDataProps } from '@/store/index'
import axios from 'axios'
import ComponentsList from '@/components/ComponentsList.vue'
import EditWrapper from '@/components/EditWrapper.vue'
import PropsTable from '@/components/PropsTable.vue'
import LayerList from '@/components/LayerList.vue'
import EditGroup from '@/components/EditGroup.vue'
import InlineEdit from '@/components/InlineEdit.vue'
import UserProfile from '@/components/UserProfile.vue'
import { ComponentData } from '@/store/editor'
import { brickTemplates, TemplateType } from '@/configs/defaultTemplates'
// imageDefaultProps 不再直接导入，模板通过接口动态获取
import useSaveWork from '@/hooks/useSaveWork'
import usePublishWork from '@/hooks/usePublishWork'
import HistoryArea from './HistoryArea.vue'
import PublishForm from './PublishForm.vue'
import PreviewForm from './PreviewForm.vue'

export type AttrTabType = 'component' | 'layer' | 'page'
export type CompTabType = 'text' | 'image' | 'shape'
export default defineComponent({
  components: {
    ComponentsList,
    EditWrapper,
    PropsTable,
    LayerList,
    EditGroup,
    HistoryArea,
    InlineEdit,
    UserProfile,
    PublishForm,
    PreviewForm,
    FieldStringOutlined,
    FileImageOutlined,
    BuildOutlined,
    ArrowLeftOutlined
  },
  setup() {
    initHotKeys()
    initContextMenu()
    const route = useRoute()
    const router = useRouter()
    const currentWorkId = computed(() => route.params.id)
    const isCreatingMode = computed(() => currentWorkId.value === 'create')
    const store = useStore<GlobalDataProps>()
    const activeAttrTab = ref<AttrTabType>('component')
    const activeCompTab = ref<CompTabType>('text')
    const components = computed(() => store.state.editor.components)
    const page = computed(() => store.state.editor.page)
    const canvasFix = ref(false)
    const showPublishForm = ref(false)
    const showPreviewForm = ref(false)
    const currentElement = computed<ComponentData | null>(() => store.getters.getCurrentElement)
    const { handleSave, saveIsLoading } = useSaveWork(isCreatingMode)
    const { publishWork, isPublishing } = usePublishWork()
    // 动态模板：初始用静态数据兜底，onMounted 时从接口获取
    const dynamicTemplates = ref<Record<TemplateType, any[]>>({
      [TemplateType.TEXT]: brickTemplates[TemplateType.TEXT],
      [TemplateType.IMAGE]: brickTemplates[TemplateType.IMAGE],
      [TemplateType.SHAPE]: brickTemplates[TemplateType.SHAPE]
    })
    onMounted(() => {
      if (!isCreatingMode.value) {
        // edit
        store.dispatch('fetchWork', { urlParams: { id: unref(currentWorkId) } })
      } else {
        // create
        store.commit('fetchWork', { data: null })
      }
      // 获取动态模板列表
      axios
        .get('/brick-templates')
        .then(resp => {
          if (resp.data.errno === 0 && resp.data.data?.list) {
            const result: Record<TemplateType, any[]> = {
              [TemplateType.TEXT]: [],
              [TemplateType.IMAGE]: [],
              [TemplateType.SHAPE]: []
            }
            resp.data.data.list.forEach((item: any) => {
              const type = item.type as TemplateType
              if (result[type]) {
                result[type].push(item.props)
              }
            })
            // 若接口某类型为空，保留静态兜底
            dynamicTemplates.value = {
              [TemplateType.TEXT]: result[TemplateType.TEXT].length
                ? result[TemplateType.TEXT]
                : brickTemplates[TemplateType.TEXT],
              [TemplateType.IMAGE]: result[TemplateType.IMAGE].length
                ? result[TemplateType.IMAGE]
                : brickTemplates[TemplateType.IMAGE],
              [TemplateType.SHAPE]: result[TemplateType.SHAPE].length
                ? result[TemplateType.SHAPE]
                : brickTemplates[TemplateType.SHAPE]
            }
          }
        })
        .catch(() => {
          // 失败时保持静态默认值
        })
    })
    const addItem = (component: any) => {
      store.commit('addComponent', component)
    }
    const setActive = (id: string) => {
      store.commit('setActive', id)
    }
    const handleChange = (e: any) => {
      store.commit('updateComponent', e)
    }
    const pageChange = (e: any) => {
      store.commit('updatePage', e)
    }
    const titleChange = (newTitle: string) => {
      store.commit('updatePage', { key: 'title', value: newTitle, isRoot: true })
    }
    const updatePosition = (data: { left: number; top: number; id: string }) => {
      const { id } = data
      const updatedData = pickBy<number>(data, (v: any, k: string) => k !== 'id')
      const keysArr = Object.keys(updatedData)
      const valuesArr = Object.values(updatedData).map(v => v + 'px')
      store.commit('updateComponent', { key: keysArr, value: valuesArr, id })
    }
    const publish = async () => {
      // remove select element
      store.commit('setActive', '')
      const el = document.getElementById('canvas-area') as HTMLElement
      canvasFix.value = true
      await nextTick()
      try {
        await publishWork(el)
        showPublishForm.value = true
      } catch (e) {
        console.error(e)
      } finally {
        canvasFix.value = false
      }
    }
    const handlePublishSuccess = () => {
      showPublishForm.value = false
    }
    const preview = async () => {
      await handleSave()
      showPreviewForm.value = true
    }
    const goBack = () => {
      router.back()
    }
    return {
      activeAttrTab,
      activeCompTab,
      components,
      isCreatingMode,
      TemplateType,
      dynamicTemplates,
      addItem,
      setActive,
      currentElement,
      handleChange,
      page,
      pageChange,
      updatePosition,
      titleChange,
      handleSave,
      saveIsLoading,
      publish,
      canvasFix,
      isPublishing,
      showPublishForm,
      preview,
      showPreviewForm,
      handlePublishSuccess,
      goBack
    }
  }
})
